import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { TrustBadge } from '@/components/TrustBadge';
import { AuthError, AuthApiError } from '@supabase/supabase-js';

const Register = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const { register } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await register(email, password, name, () => navigate('/dashboard'));
      toast({
        title: "Registration successful!",
        description: "Welcome to our platform.",
      });
    } catch (error) {
      console.error('Registration error:', error);
      let errorMessage = "Registration failed. Please try again.";
      
      if (error instanceof AuthApiError) {
        switch (error.message) {
          case 'User already registered':
            errorMessage = "An account with this email already exists. Please try logging in instead.";
            break;
          case 'Password should be at least 6 characters':
            errorMessage = "Password must be at least 6 characters long.";
            break;
          case 'Invalid email':
            errorMessage = "Please enter a valid email address.";
            break;
          default:
            errorMessage = error.message;
        }
      }
      
      toast({
        title: "Registration failed",
        description: errorMessage,
        variant: "destructive",
      });
    }
  };

  return (
    <div className="min-h-screen relative">
      <div className="absolute inset-0">
        <img 
          src="https://images.unsplash.com/photo-1529156069898-49953e39b3ac" 
          alt="Happy family enjoying time together" 
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 to-black/70" />
      </div>

      <div className="relative min-h-screen flex items-center justify-center p-4">
        <div className="w-full max-w-md space-y-8 animate-fadeIn">
          <div className="text-center space-y-2">
            <TrustBadge />
            <h2 className="text-3xl font-bold text-white">Create Your Account</h2>
            <p className="text-gray-200">Start your journey to financial freedom</p>
          </div>

          <div className="glass-card p-8 rounded-2xl backdrop-blur-md bg-white/10 border border-white/20">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-white mb-1">
                    Full Name
                  </label>
                  <Input
                    id="name"
                    type="text"
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="floating-input bg-white/10 border-white/20 text-white placeholder:text-gray-400"
                    placeholder="John Doe"
                  />
                </div>
                
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-white mb-1">
                    Email Address
                  </label>
                  <Input
                    id="email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="floating-input bg-white/10 border-white/20 text-white placeholder:text-gray-400"
                    placeholder="you@example.com"
                  />
                </div>
                
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-white mb-1">
                    Password
                  </label>
                  <Input
                    id="password"
                    type="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="floating-input bg-white/10 border-white/20 text-white placeholder:text-gray-400"
                    placeholder="••••••••"
                  />
                </div>
              </div>

              <Button type="submit" className="w-full bg-primary hover:bg-primary/90">
                Create Account
              </Button>

              <div className="mt-4 text-sm text-gray-300 space-y-2">
                <p className="text-center text-xs">
                  Protected by South African law and industry-standard encryption
                </p>
                <p className="text-center text-xs font-bold text-primary">
                  100% Free Registration - No Hidden Fees
                </p>
              </div>
            </form>

            <div className="mt-6 text-center">
              <p className="text-gray-300">
                Already have an account?{" "}
                <Link to="/login" className="font-medium text-primary hover:text-primary/80 underline">
                  Sign in
                </Link>
              </p>
            </div>
          </div>

          <div className="flex justify-center space-x-8 text-white/80">
            <div className="flex items-center space-x-2">
              <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              <span className="text-sm">Secure</span>
            </div>
            <div className="flex items-center space-x-2">
              <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              <span className="text-sm">Private</span>
            </div>
            <div className="flex items-center space-x-2">
              <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              <span className="text-sm">Fast</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;